import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Camera, Eye, EyeOff, ChevronsLeftRight } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { PhotoCard } from './PhotoCard'
import { PhotoLightbox } from './PhotoLightbox'
import { PhotoComparisonModal } from './PhotoComparisonModal'

function groupByDate(photos) {
  const groups = {}
  for (const p of photos) {
    if (!groups[p.date]) groups[p.date] = []
    groups[p.date].push(p)
  }
  // Return as array sorted desc by date
  return Object.entries(groups)
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([date, items]) => ({ date, items }))
}

export function PhotoGallery({ photos, onDelete }) {
  const { t } = useTranslation()
  const [activePhoto, setActivePhoto] = useState(null)
  const [revealedIds, setRevealedIds] = useState(() => new Set())
  const [compareOpen, setCompareOpen] = useState(false)

  const allRevealed = useMemo(() => {
    return photos.length > 0 && photos.every((p) => revealedIds.has(p.id))
  }, [photos, revealedIds])

  const toggleReveal = (id) => {
    setRevealedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (allRevealed) {
      setRevealedIds(new Set())
    } else {
      setRevealedIds(new Set(photos.map((p) => p.id)))
    }
  }

  if (photos.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Camera className="w-12 h-12 mx-auto mb-4 text-app-primary-500/50" />
          <p>{t('phoenixTracker.photos.noPhotos')}</p>
        </CardContent>
      </Card>
    )
  }

  const groups = groupByDate(photos)

  return (
    <>
      {/* Header actions */}
      <div className="flex justify-end gap-2 mb-3 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCompareOpen(true)}
          disabled={photos.length < 2}
          className="gap-2"
        >
          <ChevronsLeftRight className="w-4 h-4" />
          {t('phoenixTracker.photos.compare')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={toggleAll}
          className="gap-2"
        >
          {allRevealed ? (
            <>
              <EyeOff className="w-4 h-4" />
              {t('phoenixTracker.photos.hideAll')}
            </>
          ) : (
            <>
              <Eye className="w-4 h-4" />
              {t('phoenixTracker.photos.revealAll')}
            </>
          )}
        </Button>
      </div>

      <div className="space-y-6">
        {groups.map(({ date, items }) => (
          <div key={date}>
            <h3 className="text-sm font-mono text-muted-foreground mb-2">
              {new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {items.map((photo) => (
                <PhotoCard
                  key={photo.id}
                  photo={photo}
                  revealed={revealedIds.has(photo.id)}
                  onToggleReveal={toggleReveal}
                  onDelete={onDelete}
                  onClick={setActivePhoto}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {activePhoto && (
        <PhotoLightbox photo={activePhoto} onClose={() => setActivePhoto(null)} />
      )}

      {compareOpen && (
        <PhotoComparisonModal photos={photos} onClose={() => setCompareOpen(false)} />
      )}
    </>
  )
}
